import "./SnippetManager.css";
import { LinkRecord, Snippet } from "winslow-core";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEditingCollection,
  selectOnDiscardConfirm,
  selectSidebarSnippet,
  selectSnippets,
  selectUIStates,
  selectUser,
  setSidebarSnippet,
  setSnippets,
  setUIStates,
} from "../../redux/features/appData";
import { useEffect, useRef, useState } from "react";
import ModalSnippetDiscardChanges from "../snippets/ModalSnippetDiscardChanges";
import ModalDeleteSnippetConfirm from "./ModalDeleteSnippetConfirm";
import {
  apiCreateSnippet,
  apiDeleteSnippet,
  apiUpdateSnippet,
} from "../../services/SnippetService";
import {
  apiCreateSnippetPayload,
  apiUpdateSnippetPayload,
} from "../../services/SnippetService.types";
import { toastFail, toastSuccess } from "../../config";
import toast from "react-hot-toast";
import { useCoreDataLoader } from "../../hooks/useCoreDataLoader";
import { SnippetManagerZero } from "./SnippetManagerZero";
import { formatDate } from "winslow-core";
import { SnippetSidebar } from "../snippets/SnippetSidebar";
import { iconPath } from "winslow-core";
import { useParams } from "react-router-dom";
import { apiGetCollectionSnippets } from "../../services/CollectionService";

type SnippetManagerProps = {};

export const SnippetManager = ({}: SnippetManagerProps) => {
  const dispatch = useDispatch();
  const { reloadAll } = useCoreDataLoader(true);
  const { snippetId } = useParams();

  const selectRef = useRef<any>();

  const storeUser = useSelector(selectUser);
  const snippets = useSelector(selectSnippets);
  const uiStates = useSelector(selectUIStates);
  const onDiscardConfirm = useSelector(selectOnDiscardConfirm);
  const currentCollection = useSelector(selectEditingCollection);
  const sidebarSnippet = useSelector(selectSidebarSnippet);

  const [selectedSnippetId, setSelectedSnippetId] = useState<string>(
    snippets && snippets.length > 0 ? snippets[0].id : ""
  );
  const [snippetName, setSnippetName] = useState<string>("");
  const [snippetAnswer, setSnippetAnswer] = useState<string>("");

  const [changed, setChanged] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      if (currentCollection && currentCollection.id) {
        console.log("snippetId", snippetId);
        const snippets = await apiGetCollectionSnippets(currentCollection.id);
        if (snippetId) {
          setSelectedSnippetId(snippetId);
          dispatch(
            setUIStates({
              ...uiStates,
              snippetSidebarOpen: true,
            })
          );
        }
        dispatch(setSnippets(snippets ? [...snippets] : ([] as Snippet[])));
      }
    })();

    setUIStates({
      ...uiStates,
      snippetSidebarOpen: false,
    });
  }, []);

  useEffect(() => {
    if (snippets && selectedSnippetId) {
      console.log("selectedSnippetId", selectedSnippetId);
      const index = snippets.findIndex((snippet: Snippet) => {
        return snippet.id === selectedSnippetId;
      });

      if (index !== -1) {
        setSnippetName(snippets[index].name);
        setSnippetAnswer(snippets[index].answer);
        dispatch(setSidebarSnippet({ ...snippets[index] }));
      } else {
        toast("snippet not found", toastFail);
      }
    }
  }, [selectedSnippetId]);

  const handleAddSnippet = async () => {
    setSelectedSnippetId("__new__");
    dispatch(
      setUIStates({
        ...uiStates,
        confirmDiscardSnippetOpen: false,
        snippetEdited: false,
      })
    );
    setSnippetName("");
    setSnippetAnswer("");
  };

  const handleSwitchSnippet = (newId: string) => {
    console.log("[Switching to " + newId + "]");

    if (newId !== selectedSnippetId) {
      setChanged(true);
    }

    setSelectedSnippetId(newId);
    dispatch(
      setUIStates({
        ...uiStates,
        confirmDiscardSnippetOpen: false,
        snippetEdited: false,
      })
    );
  };

  const handleCancelNew = () => {
    if (snippets && snippets.length > 0) {
      setSelectedSnippetId(snippets[0].id);
    }

    dispatch(
      setUIStates({
        ...uiStates,
        confirmDiscardSnippetOpen: false,
        snippetEdited: false,
      })
    );
  };

  const handleDeleteSnippet = async (snippetId: string) => {
    const snippet = await apiDeleteSnippet(snippetId);

    if (!snippet) {
      toast("Delete failed.", toastFail);
      return;
    }

    toast("Snippet deleted.", toastSuccess);
    reloadAll(storeUser?.currentWorkspaceId);
    dispatch(
      setUIStates({
        ...uiStates,
        snippetEdited: false,
        confirmDeleteSnippetOpen: false,
      })
    );

    if (snippets && snippets.length > 0) {
      setSelectedSnippetId(snippets[0].id);
    }
  };

  const handleSave = async () => {
    if (!snippets || !selectedSnippetId) {
      return;
    }

    if (selectedSnippetId === "__new__") {
      await createSnippet();
      return;
    }

    const index = snippets.findIndex((snippet: Snippet) => {
      return snippet.id === selectedSnippetId;
    });

    if (index === -1) {
      return;
    }

    let snippet: Snippet | null = snippets[index];

    const payload = {
      name: snippetName,
      answer: snippetAnswer,
      question: snippet.question,
    } as apiUpdateSnippetPayload;

    snippet = await apiUpdateSnippet(selectedSnippetId, payload);

    if (!snippet) {
      toast("Problem updating snippet", toastFail);
      return;
    }

    toast("Snippet updated", toastSuccess);
    reloadAll(storeUser?.currentWorkspaceId);
    dispatch(
      setUIStates({
        ...uiStates,
        snippetEdited: false,
      })
    );
  };

  const createSnippet = async () => {
    if (!currentCollection || !sidebarSnippet) {
      return;
    }

    const linkRecordIds = sidebarSnippet.linkRecords
      ? sidebarSnippet.linkRecords.map((linkRecord: LinkRecord) => {
          return linkRecord.id;
        })
      : [];

    const payload = {
      collectionId: currentCollection?.id,
      userId: storeUser?.id,
      name: snippetName,
      answer: snippetAnswer,
      question: "",
      linkRecordIds,
    } as apiCreateSnippetPayload;

    const snippet = await apiCreateSnippet(payload);

    if (!snippet) {
      toast("Problem creating snippet", toastFail);
      return;
    }

    toast("Snippet created", toastSuccess);
    reloadAll(storeUser?.currentWorkspaceId);
    setSelectedSnippetId(snippet.id);
    dispatch(
      setUIStates({
        ...uiStates,
        snippetEdited: false,
      })
    );
  };

  const sortedSnippets = snippets ? [...snippets] : null;

  if (sortedSnippets) {
    sortedSnippets.sort((a: Snippet, b: Snippet) => {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
  }

  return (
    <>
      <div id="snippet-manager">
        <div className="page-title">Quick Reply Manager</div>

        {sortedSnippets && sortedSnippets.length > 0 && (
          <div className="snippet-manager-snippets">
            {sortedSnippets.map((snippet: Snippet) => {
              return (
                <div
                  key={"snippet-" + snippet.id}
                  className="snippet-manager-snippet"
                  onClick={() => {
                    dispatch(setSidebarSnippet({ ...snippet }));
                    dispatch(
                      setUIStates({
                        ...uiStates,
                        snippetSidebarOpen: true,
                      })
                    );
                  }}>
                  <div className="snippet-manager-snippet-title">
                    {snippet.name}
                  </div>

                  <div className="snippet-manager-snippet-metadata">
                    <div className="snippet-manager-snippet-owner">
                      <div className="snippet-manager-snippet-avatar">
                        <img src={snippet?.user?.avatarURL || ""} alt="" />
                      </div>

                      <div className="snippet-manager-snippet-name">
                        {snippet?.user
                          ? snippet.user.firstName + " " + snippet.user.lastName
                          : ""}
                      </div>
                    </div>

                    <div className="snippet-manager-snippet-updated">
                      {snippet.updatedAt && (
                        <>{formatDate(new Date(snippet.updatedAt))}</>
                      )}
                    </div>
                  </div>

                  <div
                    className="snippet-manager-buttons"
                    onClick={(e: any) => {
                      e.stopPropagation();
                      handleDeleteSnippet(snippet.id);
                    }}>
                    <img src={iconPath("trash.svg", storeUser)} alt="" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!sortedSnippets ||
          (sortedSnippets.length === 0 && <SnippetManagerZero />)}
      </div>

      {uiStates.snippetSidebarOpen && sidebarSnippet && (
        <div className="snippet-manager-sidebar">
          <SnippetSidebar allowSelect={false} />
        </div>
      )}

      {uiStates.confirmDiscardSnippetOpen && (
        <ModalSnippetDiscardChanges
          isOpen={uiStates.confirmDiscardSnippetOpen}
          onClose={() => {
            dispatch(
              setUIStates({
                ...uiStates,
                confirmDiscardSnippetOpen: false,
              })
            );

            if (selectRef) {
              selectRef.current.value = selectedSnippetId;
            }
          }}
          onContinue={() => {
            if (onDiscardConfirm) {
              onDiscardConfirm();
            }
          }}
        />
      )}

      {uiStates.confirmDeleteSnippetOpen && (
        <ModalDeleteSnippetConfirm
          isOpen={uiStates.confirmDeleteSnippetOpen}
          onClose={() => {
            dispatch(
              setUIStates({
                ...uiStates,
                confirmDeleteSnippetOpen: false,
              })
            );

            if (selectRef) {
              selectRef.current.value = selectedSnippetId;
            }
          }}
          onContinue={() => handleDeleteSnippet(selectedSnippetId)}
        />
      )}
    </>
  );
};
